import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Search, Loader2, FlaskConical, Clock, Terminal, Gauge } from "lucide-react";

const DIFFICULTY_LABELS = {
  easy: { label: "Facile", className: "text-green-500 border-green-500/40" },
  medium: { label: "Intermédiaire", className: "text-yellow-500 border-yellow-500/40" },
  hard: { label: "Avancé", className: "text-red-500 border-red-500/40" },
};

const LabsCatalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    environment: searchParams.get("environment") || "",
    difficulty: searchParams.get("difficulty") || "",
    search: searchParams.get("search") || "",
  });

  useEffect(() => {
    fetchLabs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.environment, filters.difficulty]);

  const fetchLabs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.environment) params.append("environment", filters.environment);
      if (filters.difficulty) params.append("difficulty", filters.difficulty);
      if (filters.search) params.append("search", filters.search);
      const response = await axios.get(`${API}/labs?${params.toString()}`);
      setLabs(response.data.labs || []);
    } catch (error) {
      console.error("Error fetching labs:", error);
      setLabs([]);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    const v = value === "all" ? "" : value;
    const newFilters = { ...filters, [key]: v };
    setFilters(newFilters);
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, val]) => {
      if (val) params.set(k, val);
    });
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLabs();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="labs-page">
      <Navbar />

      {/* Header */}
      <div className="border-b border-border/40 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
              <FlaskConical className="w-5 h-5 text-primary" />
            </div>
            <h1 className="font-heading font-bold text-3xl">Labos interactifs</h1>
          </div>
          <p className="text-muted-foreground max-w-2xl">
            Pratiquez sur de vrais environnements DevOps : Docker, Kubernetes, Terraform, Linux…
            Terminal, instructions guidées et validation pas à pas.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filtres */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Rechercher un lab..."
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="outline">Rechercher</Button>
          </form>
          <Select value={filters.environment || "all"} onValueChange={(v) => updateFilter("environment", v)}>
            <SelectTrigger className="w-full md:w-48"><SelectValue placeholder="Environnement" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous environnements</SelectItem>
              <SelectItem value="docker">Docker</SelectItem>
              <SelectItem value="kubernetes">Kubernetes</SelectItem>
              <SelectItem value="terraform">Terraform</SelectItem>
              <SelectItem value="ansible">Ansible</SelectItem>
              <SelectItem value="linux">Linux</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.difficulty || "all"} onValueChange={(v) => updateFilter("difficulty", v)}>
            <SelectTrigger className="w-full md:w-44"><SelectValue placeholder="Difficulté" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes difficultés</SelectItem>
              <SelectItem value="easy">Facile</SelectItem>
              <SelectItem value="medium">Intermédiaire</SelectItem>
              <SelectItem value="hard">Avancé</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Grille */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : labs.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <FlaskConical className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p>Aucun lab disponible pour le moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {labs.map((lab) => {
              const diff = DIFFICULTY_LABELS[lab.difficulty] || DIFFICULTY_LABELS.easy;
              return (
                <Link
                  key={lab.lab_id}
                  to={`/labs/${lab.lab_id}`}
                  className="group bg-card border border-border rounded-xl overflow-hidden hover:border-primary/50 transition-colors flex flex-col"
                  data-testid="lab-card"
                >
                  <div className="relative h-36 bg-gradient-to-br from-primary/20 to-background flex items-center justify-center overflow-hidden">
                    {lab.thumbnail ? (
                      <img src={lab.thumbnail} alt={lab.title} className="w-full h-full object-cover" />
                    ) : (
                      <Terminal className="w-10 h-10 text-primary/60" />
                    )}
                    <Badge className="absolute top-2 left-2 uppercase text-xs">{lab.environment}</Badge>
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <Badge variant="outline" className={`self-start mb-2 ${diff.className}`}>{diff.label}</Badge>
                    <h3 className="font-heading font-bold text-lg mb-1 group-hover:text-primary transition-colors">
                      {lab.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 flex-1">{lab.description}</p>
                    <div className="flex items-center gap-4 mt-4 pt-3 border-t border-border/60 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {lab.estimated_time_minutes} min</span>
                      <span className="flex items-center gap-1"><Gauge className="w-4 h-4" /> {lab.environment}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default LabsCatalog;
